// src/components/TopBarEspace.jsx
// Barre du haut de l'espace créateur (EspaceClient) : raccourci Profil + Notifications.
// Distinct du Navbar.jsx public (page d'accueil) pour éviter tout conflit.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IoPerson, IoNotifications, IoLogOutOutline } from "react-icons/io5";
import { FileText, Newspaper } from "lucide-react";
import { base_url } from "../utils/constante";

function authHeaders() {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    return {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
    };
}

// ══════════════════════════════════════════════════════════
//  Mapping URL → Titre affiché dans la navbar
//  (centralisé ici, plus besoin de le répéter dans chaque page)
// ══════════════════════════════════════════════════════════
function getTitreDepuisChemin(pathname) {
    // Retire le préfixe /EspaceClient
    const chemin = pathname.replace(/^\/EspaceClient\/?/, "");

    if (chemin === "") return "Dashboard";
    if (chemin === "profil") return "Mon profil";
    if (chemin === "communaute") return "Communauté";
    if (chemin === "communaute/publications") return "Communauté";
    if (chemin === "messagerie") return "Messagerie";
    if (chemin === "notifications") return "Notifications";
    if (chemin === "agenda") return "Mon agenda";
    if (chemin === "abonnement") return "Abonnement";
    if (chemin === "mabilletterie") return "Ma billetterie";
    if (chemin === "mesevents") return "Mes événements";
    if (chemin === "mesevents/ajouter") return "Ajouter un événement";
    if (chemin === "monequipes") return "Mon équipes";
    if (chemin.startsWith("mesevents/view/")) return "Détails de l'événement";
    if (chemin.startsWith("mesevents/update/")) return "Modifier l'événement";

    return "Thrundrz";
}

export default function TopBarEspace() {
    const navigate = useNavigate();
    const location = useLocation();
    const titre = getTitreDepuisChemin(location.pathname);
    const chemin = location.pathname.replace(/^\/EspaceClient\/?/, "");

    // Page Communauté : la navbar affiche les deux onglets à la place du titre
    const enCommunaute = chemin === "communaute" || chemin === "communaute/publications";
    const ongletCommunaute = chemin === "communaute/publications" ? "mes" : "fil";

    const [notifications, setNotifications] = useState([]);
    const [nonLues, setNonLues] = useState(0);
    const [ouvertNotif, setOuvertNotif] = useState(false);
    const [ouvertProfil, setOuvertProfil] = useState(false);

    const [photoProfil, setPhotoProfil] = useState(null);
    const [nomComplet, setNomComplet] = useState("");

    const notifRef = useRef(null);
    const profilRef = useRef(null);

    // ── Charger notifications ────────────────────────────
    const chargerNotifications = useCallback(async () => {
        try {
            const res = await fetch(`${base_url}notifications`, {
                headers: authHeaders(),
            });
            if (!res.ok) return;
            const data = await res.json();
            const list = data.notifications || data.data || [];
            setNotifications(list);
            setNonLues(list.filter((n) => !n.lu && !n.read_at).length);
        } catch (_) {
            // silencieux — la navbar ne doit pas casser si l'API notif n'existe pas encore
        }
    }, []);

    // ── Charger profil (photo + nom) depuis le cache local ─
    useEffect(() => {
        const photo = localStorage.getItem("photo_profil") || sessionStorage.getItem("photo_profil");
        const nom = localStorage.getItem("nomComplet") || sessionStorage.getItem("nomComplet");
        if (photo) setPhotoProfil(photo);
        if (nom) setNomComplet(nom);
        chargerNotifications();
    }, [chargerNotifications]);

    // ── Fermer les menus au clic extérieur ───────────────
    useEffect(() => {
        function handleClickOutside(e) {
            if (notifRef.current && !notifRef.current.contains(e.target)) {
                setOuvertNotif(false);
            }
            if (profilRef.current && !profilRef.current.contains(e.target)) {
                setOuvertProfil(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    function seDeconnecter() {
        localStorage.clear();
        sessionStorage.clear();
        navigate("/devenir-partenaire");
    }

    return (
        <header className="w-full bg-white border-b border-gray-200 px-4 lg:px-6 h-16 flex items-center justify-between sticky top-0 z-40">
            {/* Titre de la page — déterminé automatiquement selon l'URL         */}
            {/* Sur la page Communauté, les deux onglets remplacent le titre     */}
            {enCommunaute ? (
                <div className="flex min-w-0 items-center gap-2">
                    {/* Titre fixe — les onglets ci-dessous passent en icônes seules sur mobile */}
                    <h1 className="shrink-0 font-sigmar text-gray-900 text-base lg:hidden">
                        Communauté
                    </h1>

                    <nav className="flex items-center gap-1 overflow-x-auto">
                        <button
                            onClick={() => navigate("/EspaceClient/communaute")}
                            title="Fil communauté"
                            className={`flex shrink-0 items-center gap-2 rounded-full px-2.5 py-2 lg:px-3 text-[15px] font-semibold transition ${ongletCommunaute === "fil" ? "bg-color1 text-white" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                                }`}
                        >
                            <Newspaper size={18} />
                            <span className="hidden lg:inline">Fil communauté</span>
                        </button>
                        <button
                            onClick={() => navigate("/EspaceClient/communaute/publications")}
                            title="Mes publications"
                            className={`flex shrink-0 items-center gap-2 rounded-full px-2.5 py-2 lg:px-3 text-[15px] font-semibold transition ${ongletCommunaute === "mes" ? "bg-color1 text-white" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                                }`}
                        >
                            <FileText size={18} />
                            <span className="hidden lg:inline">Mes publications</span>
                        </button>
                    </nav>
                </div>
            ) : (
                <h1 className="text-lg  lg:text-2xl font-sigmar text-gray-900 truncate">
                    {titre}
                </h1>
            )}

            {/* Actions droite */}
            <div className="flex items-center gap-1 lg:gap-3">

                {/* 🔔 Notifications */}
                <div className="relative" ref={notifRef}>
                    <button
                        onClick={() => setOuvertNotif((v) => !v)}
                        className="relative w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition"
                    >
                        <IoNotifications size={22} className="text-gray-600" />
                        {nonLues > 0 && (
                            <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                                {nonLues > 9 ? "9+" : nonLues}
                            </span>
                        )}
                    </button>

                    {ouvertNotif && (
                        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white rounded-2xl shadow-xl border border-gray-200 z-50">
                            <div className="px-4 py-3 border-b border-gray-100 font-semibold text-gray-700 text-sm">
                                Notifications
                            </div>
                            {notifications.length === 0 ? (
                                <div className="px-4 py-8 text-center text-gray-400 text-sm">
                                    Aucune notification
                                </div>
                            ) : (
                                notifications.slice(0, 6).map((n) => (
                                    <div
                                        key={n.id}
                                        className={`px-4 py-3 border-b border-gray-50 text-sm ${!n.lu && !n.read_at ? "bg-purple-50/50" : ""
                                            }`}
                                    >
                                        <div className="font-medium  text-gray-800">
                                            {n.titre || n.title}
                                        </div>
                                        <div className="text-gray-500 text-xs mt-0.5">
                                            {n.message || n.body}
                                        </div>
                                    </div>
                                ))
                            )}
                            <button
                                onClick={() => {
                                    setOuvertNotif(false);
                                    navigate("/EspaceClient/notifications");
                                }}
                                className="w-full border-t border-gray-100 px-4 py-2.5 text-center text-sm font-semibold text-color1 hover:bg-gray-50"
                            >
                                Voir toutes les notifications
                            </button>
                        </div>
                    )}
                </div>

                {/* 👤 Profil */}
                <div className="relative" ref={profilRef}>
                    <button
                        onClick={() => setOuvertProfil((v) => !v)}
                        className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full hover:bg-gray-100 transition"
                    >
                        <span className="hidden sm:block text-sm font-medium text-gray-700 max-w-[120px] truncate">
                            {nomComplet || "Mon profil"}
                        </span>
                    </button>

                    {ouvertProfil && (
                        <div className="absolute right-0 mt-2 w-52 bg-white rounded-2xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                            <button
                                onClick={() => {
                                    setOuvertProfil(false);
                                    navigate("/EspaceClient/profil");
                                }}
                                className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition"
                            >
                                <IoPerson size={18} />
                                Mon profil
                            </button>
                            <button
                                onClick={seDeconnecter}
                                className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-500 hover:bg-red-50 transition border-t border-gray-100"
                            >
                                <IoLogOutOutline size={18} />
                                Se déconnecter
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}